import { Engine } from '@babylonjs/core/Engines/engine'
import '@babylonjs/core/Materials/Node'
import '@babylonjs/core/Materials/standardMaterial'
import '@babylonjs/core/Materials/Textures/Loaders/ddsTextureLoader'
import '@babylonjs/core/Materials/Textures/Loaders/envTextureLoader'
import { Color4 } from '@babylonjs/core/Maths/math.color'
import { DracoDecoder } from '@babylonjs/core/Meshes/Compression/dracoDecoder'
import { Logger } from '@babylonjs/core/Misc/logger'
import { Scene } from '@babylonjs/core/scene'
import '@babylonjs/loaders/glTF/2.0'
import { ShaderStore } from '@babylonjs/core/Engines/shaderStore'
import { MainCamera } from './Cameras'
import { Settings } from './Settings'
import { name, version } from './Version'
// import { log } from "../Utils/PerformanceLogging"

export class BaseViewer extends HTMLElement {
	public canvas = document.createElement('canvas')
	public engine: Engine
	public scene: Scene
	public camera: MainCamera
	private isResized = false

	public offset = { top: 0, left: 0, bottom: 0, right: 0 }

	public paused = false

	public keepAlive = false

	constructor() {
		super()
		Logger.LogLevels = Logger.NoneLogLevel

		ShaderStore.ShadersStore['pbrVertexShader'] = 'invariant gl_Position;\n' + ShaderStore.ShadersStore['pbrVertexShader']

		DracoDecoder.DefaultConfiguration = {
			wasmUrl: Settings.baseUrl + '/draco/wrapper.js',
			wasmBinaryUrl: Settings.baseUrl + '/draco/decoder.wasm',
			fallbackUrl: Settings.baseUrl + '/draco/decoder.js'
		}

		this.attachShadow({ mode: 'open' })

		this.engine = new Engine(this.canvas, true, { stencil: true, adaptToDeviceRatio: true, xrCompatible: false, preserveDrawingBuffer: true /*, antialias: false*/ }) // Generate the BABYLON 3D engine
		this.scene = new Scene(this.engine)
		this.camera = new MainCamera(this.canvas)

		this.scene.useRightHandedSystem = true
		this.scene.clearColor = new Color4(1, 1, 1, 1)

		this.shadowRoot.innerHTML = String.raw`
            <style>
                :host, canvas {
                    position: relative;
                    display: block;
                    width: 100%;
                    height: 100%;
                    overflow: hidden;
                    outline: none;
                    object-fit: contain;
					touch-action: none;
					user-select: none;
					-webkit-user-select: none; 
					-webkit-touch-callout: none; 
                }

                canvas {
                    width: 100%;
                    height: 100%;
                    outline : none;
                    transition: background-color 1s;					
                }

                .message {
                    position: absolute;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    pointer-events: none;
                }

                h1, h2 {
                    color: #656565;
                    font-weight: 100;
                    font-size: 4vw;
                    margin:0;
                    padding: 1em;
                    text-align:center;
                    text-wrap: balance;
                }

                h2 {
                    font-size: max(1vw, 12px);
                }

                h1.Precious, h2.Precious {
                    color: #baa07a;
                }
            </style>
        `

		this.shadowRoot.append(this.canvas)
		this.canvas.removeAttribute('tabindex')
	}

	connectedCallback() {
		this.setAttribute('version', name + ' ' + version)
		let timeout: number
		const observer = new ResizeObserver(() => {
			clearTimeout(timeout)
			timeout = window.setTimeout(() => {
				this.isResized = true
			}, 100)
		})
		observer.observe(this.canvas)
	}

	disconnectedCallback() {
		if (!this.keepAlive) {
			this.engine.dispose()
		}
	}

	async ready() {
		this.resize()

		// log("When Ready:", this.scene._pendingData.slice())
		await this.scene.whenReadyAsync(true)
		// log("Ready!")
		this.scene.render()
		this.dispatchEvent(new CustomEvent('ready'))
		const fps = document.getElementById('fps')
		this.engine.runRenderLoop(() => {
			if (fps) fps.innerHTML = this.engine.getFps().toFixed() + ' fps'
			if (this.isResized) {
				this.isResized = false
				this.resize()
			}
			//
			if (!this.paused || this.isResized) this.scene.render()
		})
	}

	resize() {
		this.engine.resize(false)
	}
}
customElements.define('realtime-baseviewer', BaseViewer)
